import { SettingOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Col, Divider, Modal, Row, Space, Spin, Tag, Typography } from "antd";
import { useSmartgrdEmonPowerStatusSubscription } from "pacts/app-webcore/hasura-webcore.graphql";
import React, { useMemo } from "react";
import useLocationDateTime from "../../hooks/use-location-datetime";

const { Title, Text } = Typography;

interface CTMeasurementsModalProps {
  visible: boolean;
  onClose: () => void;
  meteringPointPositionId: string | null;
  assetName?: string;
  onConfigureElectrical?: (positionId: string) => void;
}

interface ChannelMeasurement {
  type: string;
  value: number;
  timestamp: string;
  unit: string;
}

interface ChannelData {
  channelIndex: number;
  measurements: ChannelMeasurement[];
  nodeMacId: string;
  meterType: string;
}

interface NodeData {
  nodeMacId: string;
  channels: ChannelData[];
}

const CTMeasurementsModal: React.FC<CTMeasurementsModalProps> = ({
  visible,
  onClose,
  meteringPointPositionId,
  assetName,
  onConfigureElectrical,
}) => {
  const { data, loading, error } = useSmartgrdEmonPowerStatusSubscription({
    variables: { meteringPointPositionId: meteringPointPositionId! },
    skip: !meteringPointPositionId || !visible,
  });

  const { formatDateTime } = useLocationDateTime();

  const measurements = useMemo(() => {
    return data?.smartgrdEmonPowerStatus || [];
  }, [data]);

  // Helper function to get appropriate unit for measurement type
  const getMeasurementUnit = (measurementType: string): string => {
    const type = measurementType?.toLowerCase();
    switch (type) {
      case "power":
        return "W";
      case "energy":
      case "consumed_energy":
        return "kWh";
      case "current":
        return "A";
      case "voltage":
        return "V";
      case "frequency":
        return "Hz";
      case "power_factor":
        return "";
      case "temperature":
        return "°C";
      case "flow_rate":
        return "L/min";
      case "pressure":
        return "kPa";
      default:
        return "";
    }
  };

  // Helper function to format measurement type for display
  const formatMeasurementType = (measurementType: string): string => {
    return (
      measurementType
        ?.replace(/_/g, " ")
        ?.split(" ")
        ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        ?.join(" ") || "Unknown"
    );
  };

  // Organize measurements by node and channel
  const organizedData = useMemo(() => {
    const nodeMap = new Map<string, NodeData>();

    measurements.forEach((measurement) => {
      const nodeMacId = measurement.nodeMacId || "Unknown Node";
      const channelIndex = measurement.channelIndex ?? 0;

      if (!nodeMap.has(nodeMacId)) {
        nodeMap.set(nodeMacId, {
          nodeMacId,
          channels: [],
        });
      }

      const nodeData = nodeMap.get(nodeMacId)!;
      let channelData = nodeData.channels.find((ch) => ch.channelIndex === channelIndex);

      if (!channelData) {
        channelData = {
          channelIndex,
          measurements: [],
          nodeMacId,
          meterType: measurement.meterType || "CT",
        };
        nodeData.channels.push(channelData);
      }

      // Add measurement to channel
      if (measurement.lastPowerReading !== null && measurement.lastPowerReading !== undefined) {
        channelData.measurements.push({
          type: measurement.measurementType || "unknown",
          value: measurement.lastPowerReading,
          timestamp: measurement.measurementTimestamp || "",
          unit: getMeasurementUnit(measurement.measurementType || ""),
        });
      }
    });

    // Sort channels by index for consistent display
    nodeMap.forEach((nodeData) => {
      nodeData.channels.sort((a, b) => a.channelIndex - b.channelIndex);
      // Sort measurements within each channel
      nodeData.channels.forEach((channel) => {
        channel.measurements.sort((a, b) => a.type.localeCompare(b.type));
      });
    });

    return Array.from(nodeMap.values());
  }, [measurements]);

  // Helper function to get consistent color for measurement types
  const getMeasurementColor = (): string => {
    return "#262626"; // Professional dark gray color
  };

  const renderChannelCard = (channel: ChannelData, nodeId: string) => (
    <Card
      key={`${nodeId}-${channel.channelIndex}`}
      size="small"
      title={
        <Space>
          <ThunderboltOutlined />
          <span>Channel {channel.channelIndex + 1}</span>
          <Tag color="blue">{channel.meterType}</Tag>
        </Space>
      }
      style={{ marginBottom: 16 }}
      bodyStyle={{ padding: "12px 16px" }}
    >
      {channel.measurements.length > 0 ? (
        <Space direction="vertical" style={{ width: "100%" }} size="small">
          {channel.measurements.map((measurement, idx) => (
            <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ flex: 1 }}>
                <Text strong style={{ color: getMeasurementColor() }}>
                  {formatMeasurementType(measurement.type)}
                </Text>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "16px", fontWeight: 600, color: getMeasurementColor() }}>
                  {Number(measurement.value).toFixed(2)}
                  <span style={{ fontSize: "12px", marginLeft: 4, color: "#666" }}>{measurement.unit}</span>
                </div>
                {measurement.timestamp && (
                  <Text type="secondary" style={{ fontSize: "11px" }}>
                    {formatDateTime(measurement.timestamp)}
                  </Text>
                )}
              </div>
            </div>
          ))}
        </Space>
      ) : (
        <div style={{ textAlign: "center", color: "#999", padding: "20px 0" }}>
          <Text type="secondary">No measurement data</Text>
        </div>
      )}
    </Card>
  );

  const renderNodeSection = (nodeData: NodeData) => (
    <div key={nodeData.nodeMacId} style={{ marginBottom: 24 }}>
      <div style={{ marginBottom: 16, padding: "12px 16px", backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
        <Space>
          <Text strong>Node MAC ID:</Text>
          <Text code style={{ fontSize: "12px" }}>
            {nodeData.nodeMacId}
          </Text>
          <Tag color="green">
            {nodeData.channels.length} Channel{nodeData.channels.length !== 1 ? "s" : ""}
          </Tag>
        </Space>
      </div>

      <Row gutter={[16, 16]}>
        {nodeData.channels.map((channel) => (
          <Col xs={24} sm={12} lg={8} key={channel.channelIndex}>
            {renderChannelCard(channel, nodeData.nodeMacId)}
          </Col>
        ))}
      </Row>

      {nodeData !== organizedData[organizedData.length - 1] && <Divider style={{ margin: "24px 0" }} />}
    </div>
  );

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            CT Node Measurements
          </Title>
          {assetName && (
            <Text type="secondary" style={{ fontSize: "14px" }}>
              {assetName}
            </Text>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={
        <Space>
          {onConfigureElectrical && meteringPointPositionId && (
            <Button
              type="default"
              icon={<SettingOutlined />}
              onClick={() => onConfigureElectrical(meteringPointPositionId)}
            >
              Configure Electrical Parameters
            </Button>
          )}
          <Button type="primary" onClick={onClose}>
            Close
          </Button>
        </Space>
      }
      width={1200}
      destroyOnClose
    >
      <Spin spinning={loading} tip="Loading measurements...">
        {error && (
          <Alert
            message="Error loading measurements"
            description={error.message}
            type="error"
            style={{ marginBottom: 16 }}
          />
        )}

        {!loading && !error && measurements.length === 0 && (
          <Alert
            message="No measurements available"
            description="No measurement data found for this CT node."
            type="info"
            style={{ marginBottom: 16 }}
          />
        )}

        {!loading && !error && organizedData.length === 0 && measurements.length > 0 && (
          <Alert
            message="No valid measurement data"
            description="Measurement records found but contain no valid readings."
            type="warning"
            style={{ marginBottom: 16 }}
          />
        )}

        {organizedData.length > 0 && (
          <div style={{ maxHeight: "600px", overflowY: "auto" }}>{organizedData.map(renderNodeSection)}</div>
        )}
      </Spin>
    </Modal>
  );
};

export default CTMeasurementsModal;
